import { __extends } from "tslib";
import { Vector3D, AbstractionBase, Matrix3D, ColorTransform, Point, Transform, PerspectiveProjection, CoordinateSystem } from '@awayjs/core';
import { HierarchicalProperty } from '../base/HierarchicalProperty';
import { BlendMode, Settings as StageSettings, isNativeBlend } from '@awayjs/stage';
import { AlignmentMode } from '../base/AlignmentMode';
import { OrientationMode } from '../base/OrientationMode';
import { ContainerNodeEvent } from '../events/ContainerNodeEvent';
import { View } from '../View';
var ContainerNode = /** @class */ (function (_super) {
    __extends(ContainerNode, _super);
    function ContainerNode() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._renderToImage = false;
        _this._position = new Vector3D();
        _this._matrix3D = new Matrix3D();
        _this._inverseMatrix3DDirty = true;
        _this._maskDisabled = false;
        _this._transformDisabled = false;
        _this._maskId = -1;
        _this._masks = [];
        _this._childNodes = new Array();
        _this._numChildNodes = 0;
        _this._hierarchicalPropsDirty = HierarchicalProperty.ALL;
        return _this;
    }
    Object.defineProperty(ContainerNode.prototype, "parent", {
        get: function () {
            return this._parent;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "numChildNodes", {
        get: function () {
            return this._numChildNodes;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "container", {
        get: function () {
            return this._useWeak ? this._asset.deref() : this._asset;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "pickObjectNode", {
        get: function () {
            var container = this.container;
            if (this._pickObject != container.pickObject) {
                this._pickObject = container.pickObject;
                if (this._pickObject) {
                    this._pickObjectNode = this._pool.abstractions.getAbstraction(this._pickObject);
                    if (this._pickObject.pickObjectFromTimeline)
                        this._pickObjectNode.setParent(this);
                }
                else {
                    this._pickObjectNode.setParent(null);
                    this._pickObjectNode = null;
                }
            }
            return this._pickObjectNode;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "renderToImage", {
        get: function () {
            var _a = this.container, blendMode = _a.blendMode, filters = _a.filters, cacheAsBitmap = _a.cacheAsBitmap;
            var renderToImage = this.isRenderable() && (cacheAsBitmap ||
                filters && filters.length > 0 ||
                (blendMode && blendMode !== BlendMode.NORMAL && (StageSettings.USE_NON_NATIVE_BLEND || isNativeBlend(blendMode))));
            if (this._renderToImage !== renderToImage) {
                this._renderToImage = renderToImage;
                if (!this._renderToImage)
                    this.clearLocalNode();
            }
            return this._renderToImage;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "scrollRect", {
        get: function () {
            var container = this.container;
            var scrollRect = container.scrollRect;
            if (!!this._scrollRect != !!scrollRect) {
                this._scrollRect = scrollRect;
                if (this._scrollRect) {
                    this._scrollRectNode = this._pool.getNode(container.getScrollRectPrimitive());
                    //this._scrollRectNode.container.scrollRect = this._scrollRect;
                    this._scrollRectNode.setParent(this);
                }
                else if (this._scrollRectNode) {
                    this._scrollRectNode.setParent(null);
                    this._scrollRectNode = null;
                }
            }
            return this._scrollRect;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "boundsVisible", {
        get: function () {
            return false;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "view", {
        get: function () {
            return this._pool;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "maskDisabled", {
        get: function () {
            return this._maskDisabled;
        },
        set: function (value) {
            this._maskDisabled = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContainerNode.prototype, "transformDisabled", {
        get: function () {
            return this._transformDisabled;
        },
        set: function (value) {
            if (this._transformDisabled == value)
                return;
            this._maskDisabled = value;
            this._transformDisabled = value;
        },
        enumerable: false,
        configurable: true
    });
    ContainerNode.prototype.getRoot = function (local) {
        if (local === void 0) { local = false; }
        if (this._hierarchicalPropsDirty & HierarchicalProperty.ROOT) {
            this._root = this._transformDisabled && local || !this._parent
                ? this
                : this._parent.getRoot(local);
        }
        return this._root;
    };
    ContainerNode.prototype.getScale9Container = function () {
        var _a;
        if (this._hierarchicalPropsDirty & HierarchicalProperty.SCALE9) {
            var container = this.container;
            this._scale9Container = container.scale9Grid ? container : (_a = this._parent) === null || _a === void 0 ? void 0 : _a.getScale9Container();
            this._hierarchicalPropsDirty ^= HierarchicalProperty.SCALE9;
        }
        return this._scale9Container;
    };
    /**
     *
     */
    ContainerNode.prototype.getPosition = function () {
        if (this._positionDirty) {
            var container = this.container;
            if (container._registrationMatrix3D &&
                container.alignmentMode === AlignmentMode.REGISTRATION_POINT) {
                this._position.x = -container._registrationMatrix3D._rawData[12];
                this._position.y = -container._registrationMatrix3D._rawData[13];
                this._position.z = -container._registrationMatrix3D._rawData[14];
                this._position = this.getMatrix3D().transformVector(this._position, this._position);
                /*
                this._position.decrementBy(
                    new Vector3D(
                        this._registrationPoint.x*this._scaleX,
                        this._registrationPoint.y*this._scaleY,
                        this._registrationPoint.z*this._scaleZ));
                */
            }
            else {
                this.getMatrix3D().copyColumnTo(3, this._position);
            }
            this._positionDirty = false;
        }
        return this._position;
    };
    /**
     *
     */
    ContainerNode.prototype.getInverseMatrix3D = function () {
        if (this._inverseMatrix3DDirty) {
            if (!this._inverseMatrix3D)
                this._inverseMatrix3D = new Matrix3D();
            this._inverseMatrix3DDirty = false;
            this._inverseMatrix3D.copyFrom(this.getMatrix3D());
            this._inverseMatrix3D.invert();
        }
        return this._inverseMatrix3D || (this._inverseMatrix3D = new Matrix3D());
    };
    ContainerNode.prototype.getMatrix3D = function () {
        if (this._hierarchicalPropsDirty & HierarchicalProperty.SCENE_TRANSFORM) {
            var container = this.container;
            if (!this._transformDisabled) {
                this._matrix3D.copyFrom(container.transform.matrix3D);
                if (container._registrationMatrix3D) {
                    this._matrix3D.prepend(container._registrationMatrix3D);
                    if (container.alignmentMode != AlignmentMode.REGISTRATION_POINT) {
                        this._matrix3D.appendTranslation(-container._registrationMatrix3D._rawData[12] * container.transform.scale.x, -container._registrationMatrix3D._rawData[13] * container.transform.scale.y, -container._registrationMatrix3D._rawData[14] * container.transform.scale.z);
                    }
                }
                if (this._parent)
                    this._matrix3D.append(this._parent.getMatrix3D());
                // scrollRect-masks are childs of the object that have the scrollRect applied
                // to support scrolling we need to:
                // 		- move objects with scrollRect by negative scrollRect position
                // 		- move scrollRect-masks by positive scrollRect position
                if (container.scrollRect)
                    this._matrix3D.prependTranslation(-container.scrollRect.x, -container.scrollRect.y, 0);
            }
            else {
                this._matrix3D.copyFrom(ContainerNode._nullTransform.matrix3D);
            }
            this._hierarchicalPropsDirty ^= HierarchicalProperty.SCENE_TRANSFORM;
            //TODO: refactor controller API
            if (container['_iController'])
                container['_iController'].updateController();
        }
        return this._matrix3D;
    };
    /**
     *
     */
    ContainerNode.prototype.getRenderMatrix3D = function (cameraTransform) {
        var container = this.container;
        if (container.orientationMode == OrientationMode.CAMERA_PLANE) {
            var comps = cameraTransform.decompose();
            comps[0].copyFrom(this.getPosition());
            comps[2].copyFrom(container.transform.scale);
            (this._orientationMatrix || (this._orientationMatrix = new Matrix3D())).recompose(comps);
            //add in case of registration point
            if (container._registrationMatrix3D) {
                this._orientationMatrix.prepend(container._registrationMatrix3D);
                if (container.alignmentMode != AlignmentMode.REGISTRATION_POINT)
                    this._orientationMatrix.appendTranslation(-container._registrationMatrix3D._rawData[12] * container.transform.scale.x, -container._registrationMatrix3D._rawData[13] * container.transform.scale.y, -container._registrationMatrix3D._rawData[14] * container.transform.scale.z);
            }
            return this._orientationMatrix;
        }
        return this.getMatrix3D();
    };
    ContainerNode.prototype.getColorTransform = function () {
        if (this._hierarchicalPropsDirty & HierarchicalProperty.COLOR_TRANSFORM) {
            var container = this.container;
            this._hierarchicalPropsDirty ^= HierarchicalProperty.COLOR_TRANSFORM;
            if (!this._colorTransform)
                this._colorTransform = new ColorTransform();
            if (this._parent && this._parent.getColorTransform()) {
                this._colorTransform.copyFrom(this._parent.getColorTransform());
                // we MUST prepend real transform in cached phase, but reset in cached image render phase
                this._colorTransform.prepend(container.transform.colorTransform);
            }
            else {
                this._colorTransform.copyFrom(container.transform.colorTransform);
            }
            /*
            // if we will use getter - it return empty blend in USE_UNSAFE_BLEND = false
            if ((<any> container)._blendMode === BlendMode.OVERLAY) {
                // apply 0.5 alpha for object with `overlay` because we not support it now
                this._colorTransform.alphaMultiplier *= 0.5;
            }*/
        }
        return this._colorTransform || ContainerNode.nullColorTransform;
    };
    /**
     *
     * @returns {number}
     */
    ContainerNode.prototype.getMaskId = function () {
        if (this._hierarchicalPropsDirty & HierarchicalProperty.MASK_ID) {
            var container = this.container;
            this._maskId = (container.maskId != -1)
                ? container.maskId
                : (this._parent)
                    ? this._parent.getMaskId()
                    : -1;
            this._hierarchicalPropsDirty ^= HierarchicalProperty.MASK_ID;
        }
        return this._maskId;
    };
    ContainerNode.prototype.getMasks = function (update) {
        if (update === void 0) { update = false; }
        if (!update)
            return this._masks;
        var container = this.container;
        if (container.masks) {
            var len = container.masks.length;
            this._masks.length = len;
            for (var i = 0; i < len; i++) {
                this._masks[i] = this._pool.getNode(container.masks[i]);
            }
        }
        else {
            this._masks.length = 0;
        }
        if (this.scrollRect)
            this._masks.push(this._scrollRectNode);
        return this._masks;
    };
    ContainerNode.prototype.getMaskOwners = function () {
        var _a;
        if (this._hierarchicalPropsDirty & HierarchicalProperty.MASKS) {
            var masks = this.getMasks(true);
            this._maskOwners = this._maskDisabled
                ? null
                : (((_a = this._parent) === null || _a === void 0 ? void 0 : _a.getMaskOwners()) && this.getMaskId() == -1)
                    ? masks.length
                        ? this._parent.getMaskOwners().concat([this])
                        : this._parent.getMaskOwners().concat()
                    : masks.length
                        ? [this]
                        : null;
            this._hierarchicalPropsDirty ^= HierarchicalProperty.MASKS;
        }
        return this._maskOwners;
    };
    /**
     * Converts the `point` object from the Stage(global) coordinates to the
     * display object's(local) coordinates.
     *
     * To use this method, first create an instance of the Point class. The _x_
     * and _y_ values that you assign represent global coordinates because they
     * relate to the origin(0,0) of the main display area. Then pass the Point
     * instance as the parameter to the `globalToLocal()` method. The method
     * returns a new Point object with _x_ and _y_ values that relate to the
     * origin of the display object instead of the origin of the Stage.
     *
     */
    ContainerNode.prototype.globalToLocal = function (point, target) {
        if (target === void 0) { target = null; }
        var tmp = ContainerNode._tempVector3D;
        tmp.setTo(point.x, point.y, 0);
        var pos = this.getInverseMatrix3D().transformVector(tmp, tmp);
        if (!target) {
            target = new Point();
        }
        target.x = pos.x;
        target.y = pos.y;
        return target;
    };
    /**
     * Converts a two-dimensional point from the Scene(global) coordinates to a
     * three-dimensional display object's(local) coordinates.
     *
     * <p>To use this method, first create an instance of the Vector3D class. The x,
     * y and z values that you assign to the Vector3D object represent global
     * coordinates because they are relative to the origin(0,0,0) of the scene. Then
     * pass the Vector3D object to the <code>globalToLocal3D()</code> method as the
     * <code>position</code> parameter.
     * The method returns three-dimensional coordinates as a Vector3D object
     * containing <code>x</code>, <code>y</code>, and <code>z</code> values that
     * are relative to the origin of the three-dimensional display object.</p>
     *
     */
    ContainerNode.prototype.globalToLocal3D = function (position) {
        return this.getInverseMatrix3D().transformVector(position);
    };
    /**
     * Converts the `point` object from the display object's(local) coordinates
     * to the Stage(global) coordinates.
     *
     * This method allows you to convert any given _x_ and _y_ coordinates from
     * values that are relative to the origin(0,0) of a specific display
     * object(local coordinates) to values that are relative to the origin of
     * the Stage(global coordinates).
     *
     * To use this method, first create an instance of the Point class. The _x_
     * and _y_ values that you assign represent local coordinates because they
     * relate to the origin of the display object.
     *
     * You then pass the Point instance that you created as the parameter to the
     * `localToGlobal()` method. The method returns a new Point object with _x_
     * and _y_ values that relate to the origin of the Stage instead of the
     * origin of the display object.
     *
     * @param point The name or identifier of a point created with the Point
     *              class, specifying the _x_ and _y_ coordinates as properties.
     * @param target Result point
     * @return A Point object with coordinates relative to the Stage.
     */
    ContainerNode.prototype.localToGlobal = function (point, target) {
        if (target === void 0) { target = null; }
        var tmp = ContainerNode._tempVector3D;
        tmp.setTo(point.x, point.y, 0);
        var pos = this.getMatrix3D().transformVector(tmp, tmp);
        if (!target) {
            target = new Point();
        }
        target.x = pos.x;
        target.y = pos.y;
        return target;
    };
    ContainerNode.prototype.getBoundsPrimitive = function (_pickGroup) {
        return null;
    };
    ContainerNode.prototype.init = function (container, pool) {
        _super.prototype.init.call(this, container, pool, true);
        this._root = this;
        container._initNode(this);
        container._containerNodes[pool.id] = this;
        this._hierarchicalPropsDirty = HierarchicalProperty.ALL;
    };
    ContainerNode.prototype.getLocalNode = function () {
        if (!this._localNode) {
            /**
            * projection is not simple object
            * not needed spawn it for every cached partition
            * it has 3 matrices = 100 bytes + Transform,
            * that have 4 matrices + a lot of vectors (16 bytes) = 300 bytes,
            * And this is under heavy extending. 1 projection allocate more that 4kb per instance
            */
            var projection = new PerspectiveProjection();
            projection.coordinateSystem = CoordinateSystem.LEFT_HANDED;
            projection.originX = -1;
            projection.originY = -1;
            projection.transform = new Transform();
            projection.transform.moveTo(0, 0, -1000);
            projection.transform.lookAt(new Vector3D());
            var view = new View(projection, this.view.stage);
            view.backgroundAlpha = 0;
            this._localNode = view.getNode(this.container);
            this._localNode.transformDisabled = true;
            this._localNode.setParent(this);
        }
        return this._localNode;
    };
    ContainerNode.prototype.clearLocalNode = function () {
        if (this._localNode) {
            this._localNode.onClear();
            this._localNode = null;
        }
    };
    ContainerNode.prototype.onClear = function () {
        var container = this.container;
        if (container)
            delete container._containerNodes[this.view.id];
        this._localNode = null;
        this._maskOwners = null;
        // for (let i: number = 0; i < this._masks.length; i++)
        // 	this._masks[i].onClear();
        this._masks.length = 0;
        if (this._pickObject) {
            this._pickObject = null;
            this._pickObjectNode.setParent(null);
            this._pickObjectNode = null;
        }
        // for (let i: number = 0; i < this._numChildNodes; i++)
        // 	this._childNodes[i].onClear();
        this._childNodes.length = 0;
        this._numChildNodes = 0;
        this._scrollRect = null;
        this._scrollRectNode = null;
        this._renderToImage = false;
        this._isDragEntity = false;
        this._positionDirty = false;
        this._scale9Container = null;
        this._inverseMatrix3DDirty = true;
        this._maskDisabled = false;
        this._transformDisabled = false;
        this._parent = null;
        this._root = null;
        _super.prototype.clear.call(this);
        _super.prototype.onClear.call(this);
    };
    ContainerNode.prototype.onInvalidate = function () {
        this.invalidate();
    };
    ContainerNode.prototype.clear = function () {
        _super.prototype.clear.call(this);
        this._maskOwners = null;
        for (var i = 0; i < this._masks.length; i++)
            this._masks[i].clear();
        if (this._localNode)
            this._localNode.clear();
        for (var i = 0; i < this._numChildNodes; i++)
            this._childNodes[i].clear();
        if (this._pickObject)
            this._pickObjectNode.clear();
    };
    ContainerNode.prototype.isInFrustum = function (_rootEntity, _planes, _numPlanes, _pickGroup) {
        return !this.isInvisible();
    };
    ContainerNode.prototype.invalidate = function () {
        if (this._invalid)
            return;
        this._invalid = true;
        _super.prototype.invalidate.call(this);
        if (this._parent)
            this._parent.invalidate();
    };
    /**
     * @internal
     */
    ContainerNode.prototype.isInvisible = function () {
        var _a;
        if (this._hierarchicalPropsDirty & HierarchicalProperty.VISIBLE) {
            this._invisible = this._transformDisabled
                ? false
                : !this.container.visible || ((_a = this.parent) === null || _a === void 0 ? void 0 : _a.isInvisible());
            this._hierarchicalPropsDirty ^= HierarchicalProperty.VISIBLE;
        }
        return this._invisible;
    };
    ContainerNode.prototype.isIntersectingRay = function (_rootEntity, _rayPosition, _rayDirection, _pickGroup) {
        return true;
    };
    /**
     *
     * @returns {boolean}
     */
    ContainerNode.prototype.isRenderable = function () {
        // if container is invisible - all child nodes automatically invisible to
        return this.getMaskId() != -1 || !this.isInvisible() && this.getColorTransform()._isRenderable();
    };
    /**
     *
     * @returns {boolean}
     */
    ContainerNode.prototype.isCastingShadow = function () {
        return true;
    };
    /**
     * @param traverser
     */
    ContainerNode.prototype.acceptTraverser = function (traverser) {
        this._invalid = false;
        //get the sub-traverser for the partition, if different, terminate this traversal
        if (traverser.node != this && traverser !== traverser.getTraverser(this))
            return;
        if (!traverser.enterNode(this))
            return;
        traverser.applyEntity(this);
        for (var i = 0; i < this._numChildNodes; i++)
            this._childNodes[i].acceptTraverser(traverser);
    };
    ContainerNode.prototype.addChildAt = function (entity, index) {
        var node = this._pool.abstractions.getAbstraction(entity);
        node.setParent(this);
        if (index == this._numChildNodes)
            this._childNodes.push(node);
        else
            this._childNodes.splice(index, 0, node);
        this._numChildNodes++;
        this.invalidate();
        return node;
    };
    ContainerNode.prototype.removeChildAt = function (index) {
        this._numChildNodes--;
        var node = (index === this._numChildNodes)
            ? this._childNodes.pop()
            : this._childNodes.splice(index, 1)[0];
        node.setParent(null);
        this.invalidate();
        return node;
    };
    ContainerNode.prototype.getChildAt = function (index) {
        return this._childNodes.length > index ? this._childNodes[index] : null;
    };
    ContainerNode.prototype.startDrag = function () {
        this._isDragEntity = true;
    };
    ContainerNode.prototype.stopDrag = function () {
        this._isDragEntity = false;
    };
    ContainerNode.prototype.isDragEntity = function () {
        return this._isDragEntity;
    };
    ContainerNode.prototype.isMouseDisabled = function () {
        var _a;
        return this.isInvisible() || !this.container.mouseEnabled || ((_a = this.parent) === null || _a === void 0 ? void 0 : _a.isMouseChildrenDisabled());
    };
    ContainerNode.prototype.isMouseChildrenDisabled = function () {
        var _a;
        if (this._hierarchicalPropsDirty & HierarchicalProperty.MOUSE_ENABLED) {
            this._mouseChildrenDisabled = !this.container.mouseChildren || ((_a = this.parent) === null || _a === void 0 ? void 0 : _a.isMouseChildrenDisabled());
            this._hierarchicalPropsDirty ^= HierarchicalProperty.MOUSE_ENABLED;
        }
        return this._mouseChildrenDisabled;
    };
    ContainerNode.prototype.isDescendant = function (node) {
        var parent = this;
        while (parent.parent) {
            parent = parent.parent;
            if (parent == node)
                return true;
        }
        return false;
    };
    ContainerNode.prototype.invalidateHierarchicalProperty = function (property) {
        var propertyDirty = (this._hierarchicalPropsDirty ^ property) & property;
        if (!propertyDirty)
            return;
        this._hierarchicalPropsDirty |= propertyDirty;
        for (var i = 0; i < this._childNodes.length; ++i)
            this._childNodes[i].invalidateHierarchicalProperty(property);
        if (this._pickObjectNode)
            this._pickObjectNode.invalidateHierarchicalProperty(property);
        if (this._scrollRectNode)
            this._scrollRectNode.invalidateHierarchicalProperty(property);
        if (property & HierarchicalProperty.COLOR_TRANSFORM) {
            this.dispatchEvent(this._invalidateColorTransformEvent
                || (this._invalidateColorTransformEvent = new ContainerNodeEvent(ContainerNodeEvent.INVALIDATE_COLOR_TRANSFORM)));
        }
        if (property & HierarchicalProperty.SCENE_TRANSFORM) {
            this._positionDirty = true;
            this._inverseMatrix3DDirty = true;
            this.dispatchEvent(this._invalidateMatrix3DEvent
                || (this._invalidateMatrix3DEvent = new ContainerNodeEvent(ContainerNodeEvent.INVALIDATE_MATRIX3D)));
            this.invalidate();
        }
    };
    ContainerNode.prototype.setParent = function (parent) {
        if (this._parent)
            this.clear();
        this._parent = parent;
        this.invalidateHierarchicalProperty(HierarchicalProperty.ALL);
    };
    ContainerNode._nullTransform = new Transform();
    ContainerNode._tempVector3D = new Vector3D();
    ContainerNode.nullColorTransform = new ColorTransform();
    return ContainerNode;
}(AbstractionBase));
export { ContainerNode };
